import React, { Component } from 'react';
import { Container, Row, Col, Input, Button, Table, Spinner } from 'reactstrap';
import { DIMENSIONS } from './util/utilities';
import { FaSort } from 'react-icons/fa';
import AuthFunctions from './services/AuthService';
import apiFetch from './services/ApiClient';
import isEmpty from './util/isEmpty';

export class ViewFiles extends Component {
    static displayName = ViewFiles.name;

    constructor(props) {
        super(props);

        this.state = {
            search: null,
            files: [],
            isAdmin: false,           // safer default
            adminMyFilesOnly: false,  // NEW
            height: 0,
            width: 0,
            sort: {
                column: null,
                direction: 'desc'
            },
            loading: false,
            loadingDocument: false,
            noResults: false,
            currentPage: 1,
            itemsPerPage: 10,
            numberOfPages: 0,
            fileChosen: false,
            modalOpen: false,
            error: null
        };

        this.handleChange = this.handleChange.bind(this);
        this.handlePageSizeChange = this.handlePageSizeChange.bind(this);
        this.handleAdminMyFilesToggle = this.handleAdminMyFilesToggle.bind(this);
    }

    async componentDidMount() {
        const isAuthenticated = await AuthFunctions.getAuthenticatedAsync();
        if (!isAuthenticated) {
            this.props.history.push('/login');
            return;
        }

        try {
            const isAdmin = await AuthFunctions.isAdminAsync();
            this.setState({ isAdmin: isAdmin === true });
        } catch {
            this.setState({ isAdmin: false });
        }

        await this.loadFiles();
    }

    extractApiErrorMessage(data, fallback) {
        if (!data) return fallback;
        if (typeof data === 'string') return data;
        if (data.message) return data.message;
        return fallback;
    }

    handleChange(event) {
        this.setState({ currentPage: Number(event.target.value) });
    }

    handlePageSizeChange(event) {
        let pageSize = Number(event.target.value);
        this.setState({ itemsPerPage: pageSize });
        if (this.state.currentPage > Math.ceil(this.state.files.length / pageSize)) {
            this.setState({ currentPage: Math.ceil(this.state.files.length / pageSize) });
        }
    }

    async handleAdminMyFilesToggle(e) {
        const checked = !!e.target.checked;

        // Reset paging + keep UX consistent
        await this.setState({ adminMyFilesOnly: checked, currentPage: 1 });

        // Reload according to current search state
        if (this.state.search && this.state.search.trim().length > 0) {
            await this.searchFiles();
        } else {
            await this.loadFiles();
        }
    }

    render() {
        document.title = "View Files - QDraw, Inc.";

        const indexOfLastItem = this.state.currentPage * this.state.itemsPerPage;
        const indexOfFirstItem = indexOfLastItem - this.state.itemsPerPage;
        const currentItems = this.state.files.slice(indexOfFirstItem, indexOfLastItem);

        const renderItems = currentItems.map((project, i) => {
            return (
                <tr key={i}>
                    <td>{project.userType}</td>
                    <td>
                        <a href={'../FileDetails?file=' + project.fileNumber} rel="noopener noreferrer">
                            {project.fileNumber}
                        </a>
                    </td>
                    <td>{project.propertyStreetAddress}</td>
                    <td>{project.propertyOwner}</td>
                    <td>{project.lender}</td>
                    <td>{project.generalContractor}</td>
                </tr>
            );
        });

        const pageNumbers = [];
        for (let i = 1; i <= Math.ceil(this.state.files.length / this.state.itemsPerPage); i++) {
            pageNumbers.push(i);
        }

        const renderPageNumbers = pageNumbers.map(number => {
            return (
                <option key={number} value={number}>{number}</option>
            );
        });

        // --- layout styles (inline, so it's truly drop-in) ---
        const topBarRowStyle = { marginTop: '20px' };

        // Left block: Search input + button (wraps nicely on narrow widths)
        const leftControlsStyle = {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '12px'
        };

        const searchInputStyle = {
            flex: '1 1 280px',
            minWidth: '220px',
            maxWidth: '320px'   // NEW: prevents the input from taking over the row too early
        };

        const searchButtonStyle = {
            flex: '0 0 120px',
            width: '120px',
            whiteSpace: 'nowrap'
        };

        // Right block: Admin toggle + paging controls
        // Critical bits:
        // - flexWrap allows wrapping when needed
        // - justifyContent keeps them right-aligned on wide screens
        // - gap creates consistent spacing (no &nbsp games)
        // - marginTop gives a little separation when it wraps under the left block
        const rightControlsStyle = {
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '22px',
            marginTop: '6px'
        };

        const checkboxLabelStyle = {
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            whiteSpace: 'nowrap',
            flex: '0 0 auto'
        };

        const controlGroupStyle = {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            whiteSpace: 'nowrap',
            flex: '0 0 auto'
        };

        const labelStyle = {
            margin: 0,
            lineHeight: '32px' // aligns with select height visually
        };

        return (
            <Container>
                {/* TOP CONTROLS */}
                <Row style={topBarRowStyle}>
                    {/* Left: Search */}
                    <Col xl="6" lg="6" md="12" sm="12" xs="12">
                        <div style={leftControlsStyle}>
                            <Input
                                type="text"
                                onChange={(e) => this.setState({ search: e.target.value })}
                                name="search"
                                id="search"
                                onKeyPress={(e) => this.handleKeyPress(e)}
                                placeholder="Search..."
                                style={searchInputStyle}
                            />

                            <Button
                                color="primary"
                                onClick={() => this.searchFiles()}
                                style={searchButtonStyle}
                                disabled={this.state.loading ? true : false}
                            >
                                Search
                            </Button>
                        </div>
                    </Col>

                    {/* Right: My Files Only + Paging */}
                    <Col xl="6" lg="6" md="12" sm="12" xs="12">
                        <div style={rightControlsStyle}>
                            {this.state.isAdmin && (
                                <label style={checkboxLabelStyle}>
                                    <input
                                        type="checkbox"
                                        checked={this.state.adminMyFilesOnly}
                                        onChange={this.handleAdminMyFilesToggle}
                                    />
                                    <span style={{ lineHeight: '32px' }}>My Files Only</span>
                                </label>
                            )}

                            {this.state.files.length !== 0 && (
                                <>
                                    <div style={controlGroupStyle}>
                                        <label style={labelStyle}>Page</label>
                                        <select value={this.state.currentPage} onChange={this.handleChange}>
                                            {renderPageNumbers}
                                        </select>
                                    </div>

                                    <div style={controlGroupStyle}>
                                        <label style={labelStyle}>Page Size</label>
                                        <select value={this.state.itemsPerPage} onChange={this.handlePageSizeChange}>
                                            <option value="10">10</option>
                                            <option value="25">25</option>
                                            <option value="50">50</option>
                                            <option value="100">100</option>
                                        </select>
                                    </div>
                                </>
                            )}
                        </div>
                    </Col>
                </Row>

                {this.state.error && (
                    <Row style={{ marginTop: '10px' }}>
                        <Col>
                            <div className="alert alert-danger">{this.state.error}</div>
                        </Col>
                    </Row>
                )}

                {/* TABLE */}
                <Row style={{ marginTop: '20px' }}>
                    <Col xl="12" lg="12" md="12" sm="12" xs="12">
                        <Table responsive>
                            <thead>
                                <tr>
                                    <th>
                                        Type
                                        <FaSort
                                            color={this.state.sort.column === 'userType' ? '#0a58ca' : 'black'}
                                            onClick={() => this.handleSort('userType')}
                                            className="sort-button"
                                        />
                                    </th>
                                    <th>
                                        File #
                                        <FaSort
                                            color={this.state.sort.column === 'fileNumber' ? '#0a58ca' : 'black'}
                                            onClick={() => this.handleSort('fileNumber')}
                                            className="sort-button"
                                        />
                                    </th>
                                    <th>
                                        Property Address
                                        <FaSort
                                            color={this.state.sort.column === 'propertyStreetAddress' ? '#0a58ca' : 'black'}
                                            onClick={() => this.handleSort('propertyStreetAddress')}
                                            className="sort-button"
                                        />
                                    </th>
                                    <th>
                                        Owner Name
                                        <FaSort
                                            color={this.state.sort.column === 'propertyOwner' ? '#0a58ca' : 'black'}
                                            onClick={() => this.handleSort('propertyOwner')}
                                            className="sort-button"
                                        />
                                    </th>
                                    <th>
                                        Lender Name
                                        <FaSort
                                            color={this.state.sort.column === 'lender' ? '#0a58ca' : 'black'}
                                            onClick={() => this.handleSort('lender')}
                                            className="sort-button"
                                        />
                                    </th>
                                    <th>
                                        Contractor
                                        <FaSort
                                            color={this.state.sort.column === 'generalContractor' ? '#0a58ca' : 'black'}
                                            onClick={() => this.handleSort('generalContractor')}
                                            className="sort-button"
                                        />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.state.loading === true && this.state.loadingDocument === false ? (
                                    <tr>
                                        <td style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center', minHeight: '50vh', position: 'absolute', left: '50%', marginTop: '50px', marginLeft: '-50px' }}>
                                            <Spinner color="primary" children="" />
                                        </td>
                                    </tr>
                                ) : (
                                    <>
                                        {!this.state.noResults ? (
                                            <>
                                                {renderItems}
                                            </>
                                        ) : (
                                            <tr>
                                                <td colSpan="6" style={{ textAlign: 'center', paddingTop: '30px' }}>
                                                    <h5>No Results Found</h5>
                                                </td>
                                            </tr>
                                        )}
                                    </>
                                )}
                            </tbody>
                        </Table>
                    </Col>
                </Row>

                {/* BOTTOM PAGER (kept as you had it) */}
                {this.state.files.length !== 0 && (
                    <div style={{ display: 'flex', justifyContent: 'right', alignItems: 'center', textAlign: 'center', marginTop: '20px', marginBottom: '50px', gap: '10px', flexWrap: 'wrap' }}>
                        <label style={{ margin: 0, lineHeight: '32px' }}>Page</label>
                        <select value={this.state.currentPage} onChange={this.handleChange}>
                            {renderPageNumbers}
                        </select>
                        <label style={{ margin: 0, lineHeight: '32px' }}>Page Size</label>
                        <select value={this.state.itemsPerPage} onChange={this.handlePageSizeChange}>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                )}
            </Container>
        );
    }

    handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            this.searchFiles();
        }
    }

    async searchFiles() {
        const query = this.state.search ? this.state.search : '';

        if (query === '') {
            await this.loadFiles();
            return;
        }

        this.setState({ loading: true, loadingDocument: false, error: null });

        const mineParam = (this.state.isAdmin && this.state.adminMyFilesOnly) ? '&mine=true' : '';
        const r = await apiFetch(`api/Files/SearchFiles?query=${encodeURIComponent(query)}${mineParam}`, {
            method: 'GET'
        });

        if (r.status === 401) {
            this.setState({ loading: false });
            this.props.history.push('/login');
            return;
        }

        if (!r.ok) {
            const msg = this.extractApiErrorMessage(r.data, `Error searching files (HTTP ${r.status}).`);
            console.error(msg, r.data);
            this.setState({ loading: false, error: msg });
            return;
        }

        const data = Array.isArray(r.data) ? r.data : [];
        this.setState({ files: data, loading: false, noResults: data.length === 0 });
    }

    async loadFiles() {
        this.setState({ loading: true, loadingDocument: false, error: null });

        const mineParam = (this.state.isAdmin && this.state.adminMyFilesOnly) ? '?mine=true' : '';
        const r = await apiFetch(`api/Files/LoadFiles${mineParam}`, { method: 'GET' });

        if (r.status === 401) {
            this.setState({ loading: false });
            this.props.history.push('/login');
            return;
        }

        if (!r.ok) {
            const msg = this.extractApiErrorMessage(r.data, `Error loading files (HTTP ${r.status}).`);
            console.error(msg, r.data);
            this.setState({ loading: false, error: msg });
            return;
        }

        const data = Array.isArray(r.data) ? r.data : [];
        this.setState({ files: data, loading: false, noResults: isEmpty(data) });
    }

    handleSort(column) {
        const direction = this.state.sort.column ? (this.state.sort.direction === 'asc' ? 'desc' : 'asc') : 'desc';

        const data = [...this.state.files];
        data.sort((a, b) => (a[column] ?? '').localeCompare(b[column] ?? ''));

        if (direction === 'desc') data.reverse();

        this.setState({
            files: data,
            sort: {
                column,
                direction
            }
        });
    }
}